import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box } from "@chakra-ui/react";
import { useId } from "react";
import { SHORT_LOGO_PATH } from "./logo";
/**
 * Horizontal slices of the mark. Each band draws itself in left to right a beat
 * behind the one above it, so the glyph assembles top to bottom before the
 * whole thing wipes out. The percentages are cuts through the glyph, not even
 * thirds, so they follow the mark's own shape.
 */
const BANDS = [
    { clipPath: "inset(0 0 58.5% 0)", delay: "0s" },
    { clipPath: "inset(41.5% 0 26.3% 0)", delay: "0.11s" },
    { clipPath: "inset(73.7% 0 0 0)", delay: "0.22s" },
];
const SWEEP_EASING = "cubic-bezier(0.33, 1, 0.68, 1)";
/** lilac.600 — the accent each band sweeps in with before it settles to ink. */
const ACCENT_ON_LIGHT = "#8268C0";
/**
 * On charcoal the ramp's lilac.300 washes out, so the mark borrows the lilac
 * the top bar and the toasts already glow with. It has no ramp token of its own.
 */
const ACCENT_ON_DARK = "#B9A7FF";
const GLYPH_STYLE = { display: "block", width: "100%", height: "100%" };
/**
 * The Fluenceur mark drawing itself in three bands over a faint ghost of the
 * full glyph.
 *
 * Purely decorative — hidden from assistive tech, so whatever wraps it owns the
 * `role="status"` announcement. The motion is CSS only, which costs nothing
 * while a page is still fetching, but the keyframes live in `@fluenceur/theme`:
 * the mark only animates under a ChakraProvider fed that shared system. Under
 * `prefers-reduced-motion` it stops flat and leaves a static mark.
 */
export const LoaderMark = ({ size = 96, speed = 2200, tone = "light", wrapper = "none", accent, }) => {
    const isDark = tone === "dark";
    const duration = `${speed / 1000}s`;
    // The seven layers below are the same glyph, so it is defined once as a
    // <symbol> and stamped by reference. useId embeds colons, which a fragment
    // reference cannot carry.
    const markId = `loader-mark-${useId().replace(/:/g, "")}`;
    const glyph = (_jsx("svg", { viewBox: "0 0 24 24", style: GLYPH_STYLE, children: _jsx("use", { href: `#${markId}` }) }));
    const tile = wrapper === "card"
        ? {
            padding: `${Math.round(size * 0.3)}px`,
            bg: isDark ? "darkNav" : "white",
            borderWidth: "1px",
            borderColor: isDark ? "rgba(255, 255, 255, 0.09)" : "cardBorder",
            borderRadius: "xl",
        }
        : {};
    return (_jsx(Box, { "aria-hidden": "true", display: "inline-flex", flex: "0 0 auto", ...tile, css: {
            "--loader-ink": isDark ? "#FFFFFF" : "#1A1A1A",
            "--loader-accent": accent ?? (isDark ? ACCENT_ON_DARK : ACCENT_ON_LIGHT),
            "@media (prefers-reduced-motion: reduce)": {
                "& [data-loader-animated]": {
                    animation: "none",
                    transform: "none",
                    clipPath: "inset(0)",
                    color: "var(--loader-ink)",
                },
                "& [data-loader-glint]": { display: "none" },
            },
        }, children: _jsxs(Box, { position: "relative", width: `${size}px`, height: `${size}px`, color: "var(--loader-ink)", children: [_jsx("svg", { width: "0", height: "0", style: { position: "absolute" }, children: _jsx("symbol", { id: markId, viewBox: "0 0 24 24", children: _jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: SHORT_LOGO_PATH, fill: "currentColor" }) }) }), _jsxs(Box, { "data-loader-animated": true, position: "absolute", inset: 0, animation: `loader-settle ${duration} ${SWEEP_EASING} infinite`, children: [_jsx(Box, { position: "absolute", inset: 0, opacity: isDark ? 0.16 : 0.09, children: glyph }), BANDS.map((band) => (_jsxs(Box, { position: "absolute", inset: 0, clipPath: band.clipPath, children: [_jsx(Box, { "data-loader-animated": true, position: "absolute", inset: 0, animation: `loader-draw ${duration} ${SWEEP_EASING} ${band.delay} infinite, loader-draw-ink ${duration} linear ${band.delay} infinite`, children: glyph }), _jsx(Box, { "data-loader-glint": true, position: "absolute", inset: 0, color: "var(--loader-accent)", animation: `loader-glint ${duration} ${SWEEP_EASING} ${band.delay} infinite`, children: glyph })] }, band.delay)))] })] }) }));
};
